import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "../components/Header";
import HoverDropdown from "../components/HoverDropdown";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SessionExpiredDialog from "../components/SessionExpiredDialog";
import LoaderComp from "../components/LoaderComp";

const LoginActivity = () => {
  const { token, tokenExpiryTime } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [loginStats, setLoginStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const headers = {
    Authorization: `Bearer ${
      token === null ? localStorage.getItem("token") : token
    }`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessRes = await axios.get("/api/login-stats-access", { headers });
        if (!accessRes.data.allowed) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        const res = await axios.get("/api/login-stats", { headers });
        setLoginStats(res.data.stats || []);
      } catch (err) {
        if (err.response && err.response.status === 403) {
          setAccessDenied(true);
        }
        console.error("Failed to fetch login stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalLogins = loginStats.reduce((sum, e) => sum + e.count, 0);
  const topUser = loginStats.length > 0 ? loginStats[0] : null;

  return (
    <div className="w-full h-full">
      <Header>
        <div>
          <HoverDropdown
            title={"Login Activity"}
            content={{
              Home: () => navigate("/live_data"),
              Market: () => navigate("/market"),
              Instances: () => navigate("/instances"),
              Allocation: () => navigate("/allocation"),
              "Login Activity": () => navigate("/login-activity"),
            }}
            icon={false}
          />
        </div>
      </Header>

      {new Date().getTime() > tokenExpiryTime ? (
        <SessionExpiredDialog
          expired={new Date().getTime() > tokenExpiryTime}
        />
      ) : (
        <div className="max-w-4xl mx-auto mt-8 px-4">
          {accessDenied ? (
            <div className="text-center mt-20">
              <h2 className="text-xl font-bold text-red-600 mb-3">Access Denied</h2>
              <p className="text-gray-600 mb-4">You do not have permission to view login activity.</p>
              <button
                className="px-4 py-2 bg-sky-700 text-white rounded hover:bg-sky-800"
                onClick={() => navigate("/live_data")}
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
          <>
          <h1 className="text-2xl font-bold text-sky-700 mb-6">
            Login Activity — Last 15 Days
          </h1>

          {loading ? (
            <div className="flex justify-center items-center mt-20">
              <LoaderComp />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4 text-center">
                  <p className="text-sm text-gray-500">Total Logins</p>
                  <p className="text-3xl font-bold text-sky-700">{totalLogins}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 text-center">
                  <p className="text-sm text-gray-500">Unique Users</p>
                  <p className="text-3xl font-bold text-sky-700">{loginStats.length}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 text-center">
                  <p className="text-sm text-gray-500">Most Active User</p>
                  <p className="text-lg font-bold text-sky-700 truncate">
                    {topUser ? topUser._id : "—"}
                  </p>
                  {topUser && (
                    <p className="text-xs text-gray-400">{topUser.count} logins</p>
                  )}
                </div>
              </div>

              {loginStats.length > 0 ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-sky-700 text-white">
                        <th className="text-left px-4 py-3">#</th>
                        <th className="text-left px-4 py-3">Email</th>
                        <th className="text-center px-4 py-3">Login Count</th>
                        <th className="text-left px-4 py-3">Last Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginStats.map((entry, idx) => (
                        <tr
                          key={entry._id}
                          className={`border-b ${
                            idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                          } hover:bg-sky-50 transition-colors`}
                        >
                          <td className="px-4 py-3 font-medium">{idx + 1}</td>
                          <td className="px-4 py-3">{entry._id}</td>
                          <td className="px-4 py-3 text-center font-semibold text-sky-700">
                            {entry.count}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {new Date(entry.lastLogin).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500 mt-10">
                  No logins recorded in the last 24 hours.
                </p>
              )}
            </>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
};

export default LoginActivity;
